import "./App.css";
import { useEffect, useState } from "react";
import emailjs from "emailjs-com";
import Hs from "./components/hs";

const projects = [
  {
    title: "Backyard",
    image: "/project1.jpg",
    description: "Custom landscaping and lounge space.",
  },
  {
    title: "House",
    image: "/project2.jpg",
    description: "Full exterior refresh and siding replacement.",
  },
  {
    title: "Basement",
    image: "/project3.jpg",
    description: "Modernized basement with new flooring and lighting.",
  },
  {
    title: "Kitchen",
    image: "/project4.jpg",
    description: "Quartz counters, new cabinetry, and open layout.",
  },
  {
    title: "Bathroom",
    image: "/project5.jpg",
    description: "Walk-in shower, floating vanity, and LED mirrors.",
  },
  {
    title: "Custom Work",
    image: "/project6.jpg",
    description:
      "Bespoke finishes and tailored renovations in collaboration with design and architectural teams.",
  },
];

const projectKeyMap = {
  Backyard: "backyard",
  House: "house",
  Basement: "basement",
  Kitchen: "kitchen",
  Bathroom: "bathroom",
  "Custom Work": "custom",
};

function App() {
  const [logoHeight, setLogoHeight] = useState(300);
  const [activeProject, setActiveProject] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [navbarTinted, setNavbarTinted] = useState(false);
  const [beforeThumbnail, setBeforeThumbnail] = useState(null);
  const [afterThumbnail, setAfterThumbnail] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setNavbarTinted(true);
      } else {
        setNavbarTinted(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const loadThumbnails = async () => {
      try {
        const res = await fetch(`${process.env.PUBLIC_URL}/images.json`);
        const data = await res.json();

        // Check if before.jpg exists in public root, otherwise use first image from before folder
        const beforeImg = (await checkImageExists("/before.jpg"))
          ? "/before.jpg"
          : data.before && data.before.length > 0
          ? data.before[0]
          : null;

        const afterImg = (await checkImageExists("/after.jpg"))
          ? "/after.jpg"
          : data.after && data.after.length > 0
          ? data.after[0]
          : null;

        setBeforeThumbnail(beforeImg);
        setAfterThumbnail(afterImg);
      } catch (e) {
        console.error("Failed to load thumbnails", e);
      }
    };

    const checkImageExists = (url) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
      });
    };

    loadThumbnails();
  }, []);

  useEffect(() => {
    const updateLogoHeight = () => {
      const navbar = document.querySelector(".navbar");
      const navbarHeight = navbar ? navbar.offsetHeight : 0;
      const availableHeight = window.innerHeight - navbarHeight - 40;
      setLogoHeight(Math.max(150, availableHeight));
    };

    updateLogoHeight();
    window.addEventListener("resize", updateLogoHeight);
    return () => window.removeEventListener("resize", updateLogoHeight);
  }, []);

  const closeOverlay = () => {
    setActiveProject(null);
    setGalleryImages([]);
  };

  const handleProjectClick = async (title) => {
    const key = projectKeyMap[title];
    if (!key) {
      console.warn(`No mapping found for project: ${title}`);
      return;
    }

    try {
      const res = await fetch(`${process.env.PUBLIC_URL}/images.json`);
      const data = await res.json();
      setGalleryImages(data[key] || []);
      setActiveProject(title);
    } catch (e) {
      console.error("Failed to load images.json", e);
    }
  };

  const handleBeforeAfterClick = async (type) => {
    try {
      const res = await fetch(`${process.env.PUBLIC_URL}/images.json`);
      const data = await res.json();
      setGalleryImages(data[type] || []);
      setActiveProject(type === "before" ? "Before" : "After");
    } catch (e) {
      console.error("Failed to load images.json", e);
    }
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const sendEmail = (e) => {
    e.preventDefault();
    emailjs
      .sendForm("YOUR_SERVICE_ID", "YOUR_TEMPLATE_ID", e.target, "YOUR_USER_ID")
      .then(
        (result) => {
          console.log(result.text);
          alert("Message sent!");
        },
        (error) => {
          console.log(error.text);
          alert("Failed to send message.");
        }
      );
  };

  return (
    <div className="App">
      <header className="hero">
        <img
          src="/logo.png"
          alt="Timeless Transformations Logo"
          className="logo pulse"
          style={{
            height: "75vmin",
            width: "75vmin",
          }}
        />
      </header>

      <nav className={`navbar ${navbarTinted ? "navbar-tinted" : ""}`}>
        <span
          className="nav-link"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          Home
        </span>
        <span className="nav-link" onClick={() => scrollToSection("work")}>
          Work
        </span>
        <span
          className="nav-link"
          onClick={() => scrollToSection("beforeafter")}
        >
          B & A
        </span>
        <span className="nav-link" onClick={() => scrollToSection("about")}>
          About
        </span>
        <span className="nav-link" onClick={() => scrollToSection("services")}>
          Services
        </span>
        <span className="nav-link" onClick={() => scrollToSection("contact")}>
          Contact
        </span>
      </nav>

      <section id="work" className="section">
        <h2 className="hTwo">Our Work</h2>
        <div className="grid">
          {projects.map((project, index) => (
            <div
              key={index}
              className="card"
              onClick={() => handleProjectClick(project.title)}
            >
              <img src={project.image} alt={project.title} />
              <div className="card-text">
                <h3>{project.title}</h3>
                <p>{project.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Before & After Section */}
      <section
        id="beforeafter"
        className="section"
        style={{ backgroundColor: "#0a0a0a", padding: "6rem 2rem" }}
      >
        <h2 className="hTwo" style={{ marginBottom: "3rem" }}>
          Before & After
        </h2>
        <div className="grid" style={{ maxWidth: "800px", margin: "0 auto" }}>
          <div
            className="card"
            onClick={() => handleBeforeAfterClick("before")}
            style={{ cursor: "pointer" }}
          >
            <img src={beforeThumbnail || "/before.jpg"} alt="Before" />
            <div className="card-text">
              <h3>Before</h3>
              <p>
                See where we started - the raw potential waiting to be
                transformed.
              </p>
            </div>
          </div>
          <div
            className="card"
            onClick={() => handleBeforeAfterClick("after")}
            style={{ cursor: "pointer" }}
          >
            <img src={afterThumbnail || "/after.jpg"} alt="After" />
            <div className="card-text">
              <h3>After</h3>
              <p>
                The finished transformations - craftsmanship brought to life.
              </p>
            </div>
          </div>
        </div>
      </section>

      {activeProject && (
        <div className="overlay" onClick={closeOverlay}>
          <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
            <h3>{activeProject}</h3>

            {selectedImage ? (
              <>
                <img
                  src={selectedImage}
                  alt="Selected view"
                  style={{
                    maxWidth: "90vw",
                    maxHeight: "90vh",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    borderRadius: "0.5rem",
                  }}
                />
                <button
                  onClick={() => setSelectedImage(null)}
                  className="button"
                  style={{ marginTop: "1rem" }}
                >
                  Back to Gallery
                </button>
              </>
            ) : (
              <>
                <div
                  className="grid"
                  style={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  }}
                >
                  {galleryImages.length > 0 ? (
                    galleryImages.map((src, i) => (
                      <div
                        key={i}
                        style={{
                          width: "100%",
                          paddingTop: "100%",
                          position: "relative",
                          overflow: "hidden",
                          borderRadius: "0.5rem",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedImage(src)}
                      >
                        <img
                          src={src}
                          alt={`${activeProject} ${i + 1}`}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    ))
                  ) : (
                    <p style={{ color: "#ccc" }}>
                      No images found for this project.
                    </p>
                  )}
                </div>
                <button
                  onClick={closeOverlay}
                  className="button"
                  style={{ marginTop: "1rem" }}
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {showContactForm && (
        <div
          className="contact-popup"
          onClick={() => setShowContactForm(false)}
        >
          <div
            className="contact-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ color: "#f5c06c", marginBottom: "1rem" }}>
              CONTACT US
            </h2>

            <p style={{ color: "#ccc", lineHeight: "1.6" }}>
              📞 <strong>Rob:</strong> 647-922-5594
              <br />
              📍 <strong>Location:</strong> Woodbridge, ON
              <br />
              🕒 <strong>Hours of Operation:</strong>
              <br />
              &nbsp;&nbsp;Monday to Friday: 8:00AM – 4:00PM
              <br />
              &nbsp;&nbsp;Saturday: By Appointment Only
              <br />
              &nbsp;&nbsp;Sunday: Closed
            </p>
            <br />
            <strong>Feel free to send us a message</strong>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = {
                  name: e.target.user_name.value,
                  email: e.target.user_email.value,
                  message: e.target.message.value,
                };

                try {
                  const response = await fetch(
                    "https://formspree.io/f/mjkraagz",
                    {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify(formData),
                    }
                  );

                  if (response.ok) {
                    alert(
                      "Thanks for reaching out! We'll get back to you soon."
                    );
                    setShowContactForm(false);
                    e.target.reset();
                  } else {
                    alert(
                      "There was a problem submitting your form. Please try again."
                    );
                  }
                } catch (err) {
                  console.error("Form submission error:", err);
                  alert("An error occurred. Please try again.");
                }
              }}
              style={{ marginTop: "1.5rem" }}
            >
              <input
                className="contact-input"
                type="text"
                name="user_name"
                placeholder="Your name"
                required
              />
              <input
                className="contact-input"
                type="email"
                name="user_email"
                placeholder="Your email"
                required
              />
              <textarea
                className="contact-textarea"
                name="message"
                placeholder="Your message"
                required
              />
              <button type="submit" className="contact-submit">
                Send
              </button>
            </form>

            <button
              className="contact-close"
              onClick={() => setShowContactForm(false)}
              style={{ marginTop: "1rem" }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <section
        id="about"
        className="section dark"
        style={{ padding: "6rem 2rem", backgroundColor: "#111" }}
      >
        <div
          style={{ maxWidth: "900px", margin: "0 auto", textAlign: "center" }}
        >
          <h2
            style={{
              fontSize: "2.5rem",
              marginBottom: "2rem",
              color: "#f5c06c",
              letterSpacing: "0.05em",
            }}
          >
            Built on Passion
          </h2>

          <p
            style={{
              fontSize: "1.15rem",
              lineHeight: "2rem",
              color: "#ccc",
              textAlign: "left",
            }}
          >
            <strong>About Us</strong>
            <br />
            <em>Family-Built. Passion-Fueled. Quality-Driven.</em>
            <br />
            <br />
            At Timeless Transformation, we're more than a renovation team, we're
            a family with a deep love for design, craftsmanship, and building
            spaces that tell a story. What started as side gigs in hair salons
            quickly turned into careers in construction. Rob found his stride in
            crown molding and site management, while Andy brought business sense
            and practical grit to every job. Together, they've mastered every
            phase of home renovation, from framing to finishing, always with a
            commitment to precision, care, and client trust.
            <br />
            <br />
            Their journey is shared with Jules, Rob's stepdaughter, whose
            creative spark has given rise to{" "}
            <strong>Whiskey Fox Woodworking</strong> — the next generation of
            custom craftsmanship built on storytelling and soul.
          </p>

          <p
            style={{
              fontSize: "1.15rem",
              lineHeight: "2rem",
              color: "#ccc",
              textAlign: "left",
              marginTop: "2rem",
            }}
          >
            <strong>What We Believe In:</strong>
          </p>

          <ul
            style={{
              paddingLeft: "1.5rem",
              color: "#ccc",
              marginTop: "1rem",
              textAlign: "left",
            }}
          >
            <li>
              • <strong>Professionalism</strong> – We treat every home like our
              own.
            </li>
            <li>
              • <strong>Craftsmanship</strong> – The smallest details matter
              most.
            </li>
            <li>
              • <strong>Relationships</strong> – Trust and respect guide
              everything we build.
            </li>
          </ul>

          <p
            style={{
              fontSize: "1.15rem",
              lineHeight: "2rem",
              color: "#ccc",
              textAlign: "left",
              marginTop: "2rem",
            }}
          >
            With over a decade of experience and a love for what we do, Timeless
            Transformation is here to help reimagine your home — one project,
            one room, one vision at a time.
          </p>
        </div>
      </section>

      <br />
      <br />

      <section
        id="services"
        className="section dark"
        style={{ padding: "6rem 2rem", backgroundColor: "#111" }}
      >
        <div style={{ maxWidth: "900px", margin: "0 auto", textAlign: "left" }}>
          <h2
            className="hTwo"
            style={{
              color: "#f5c06c",
              marginBottom: "2rem",
              textAlign: "center",
            }}
          >
            Our Services
          </h2>
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              color: "#ccc",
              lineHeight: "2rem",
            }}
          >
            {[
              {
                title: "Custom Backyard Builds",
                desc: "Landscaping, patios, decks, and lounge spaces designed for comfort and style.",
              },
              {
                title: "Exterior Home Upgrades",
                desc: "Siding replacement, exterior painting, and curb appeal enhancements.",
              },
              {
                title: "Basement Renovations",
                desc: "Complete transformations including flooring, lighting, framing, and drywall.",
              },
              {
                title: "Kitchen Remodels",
                desc: "Open concept layouts, custom cabinetry, countertops, and modern appliances.",
              },
              {
                title: "Bathroom Upgrades",
                desc: "Walk-in showers, vanities, tiling, and smart lighting solutions.",
              },
              {
                title: "Interior Design + Trim",
                desc: "Furniture placement, finish selection, crown molding, and feature walls.",
              },
              {
                title: "Project Management",
                desc: "From permits to final walkthrough, we coordinate trades and timelines with precision.",
              },
            ].map((item, i) => (
              <li key={i} style={{ marginBottom: "1.5rem" }}>
                <strong>{item.title}</strong> — {item.desc}
              </li>
            ))}
          </ul>
        </div>
      </section>
      <br />
      <br />
      <section
        id="contact"
        className="sectionDark"
        style={{ padding: "6rem 2rem", height: "60vh" }}
      >
        <h2 className="hTwo">Let's Build Something Beautiful</h2>
        <button className="button" onClick={() => setShowContactForm(true)}>
          Contact Us
        </button>
      </section>

      <footer>
        &copy; {new Date().getFullYear()} Timeless Transformation Inc.
        <Hs />
      </footer>
    </div>
  );
}

export default App;
